import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { clarionDoorURL } from '../../../../constants/external-links';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { policyType as policyTypeLookup } from '@ourbranch/lookups';
import { IconButton } from '@material-ui/core';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import { isBranchEnv } from 'brand-config';
import { DRAFT_STATUS, getDraftStatus, isDraftablePolicyType } from 'core/helpers/policy-drafting';
import { withStore } from 'core/store';
import InfoIcon from 'core/assets/svg/ic-help.svg';
import icLocked from 'core/assets/svg/icLocked.svg';
import icMenu from 'core/assets/svg/ic-menu-icon.svg';
import { StatusPill } from 'core/components/status-pill';
import { Tooltip } from 'core/components/tooltip';
import ValueField from 'core/components/value-field';
import equalsIcon from 'core/assets/svg/equals.svg';
import timesIcon from 'core/assets/svg/times.svg';
import { Card } from 'core/components/card';
import { capitalizeWords, currencyFormatter, dateFormatter, laymanDateFormatter } from 'core/helpers/formatters';
import { AutoBreakdownPreview } from 'common/components/auto/auto-breakdown-preview';
import { useToast } from 'core/components/toast';
import { PolicyModalTypes } from 'core/store/account-store/policies-store/policy-store/policy-store';
import { GET_RATING_WORKSHEET, WORKSHEET_RERATE_MESSAGE, isRerateError } from 'core/store/offer-store/offer-queries';

import useSession from 'core/hooks/use-session';
import MutualSegmentContent from './mutual-segment-content';
import useStyles from '../policy-sidebar.styles';

const isMutual = !isBranchEnv;

const avoidKeys = ['startDate', 'segmentId', '__typename', 'clarionDoorId', 'farmClarionDoorIds', 'clarionDoorRelease'];

const getChanges = (object) => {
  const keys = Object.keys(object);
  return keys
    .filter((key) => !avoidKeys.includes(key) && !!object[key])
    .map((key) => capitalizeWords(key))
    .join(', ');
};

function getFarmClarionDoorIds(farmClarionDoorIds) {
  const ids = [];
  if (farmClarionDoorIds?.liabilityClarionDoorId) {
    ids.push(farmClarionDoorIds.liabilityClarionDoorId);
  }
  if (farmClarionDoorIds?.risksClarionDoorIds) {
    for (const risk of farmClarionDoorIds.risksClarionDoorIds) {
      ids.push(risk.clarionDoorId);
    }
  }
  return ids;
}

const SegmentItem = ({
  store: {
    account: {
      policies: {
        policy: {
          switchSegment,
          setActivePolicyModal,
          downloadAutoPremiumBreakdownPdf,
          downloadingAutoPremiumBreakdown,
          preview,
          policy: { offer, policyDetails, premiumBreakdown, id: policyId, accountId: policyAccountId }
        }
      }
    }
  },
  segment,
  policyType,
  active,
  lastChild,
  onClick,
  isDraft,
  isNewChange
}) => {
  // Locking segments while a draft is open is part of the drafting UX, not mutual branding.
  const isLocked = isDraftablePolicyType(policyType) && !!preview && !isDraft;
  const classes = useStyles({ isDraft, isInPolicyPreviewMode: !!preview, active, isMutual, isLocked });
  const accountId = policyAccountId || (policyId ? policyId.split('-')[0] : null);
  const toast = useToast();
  const { isAgency, canApproveUWReview } = useSession();
  const [fetchRatingWorksheet] = useLazyQuery(GET_RATING_WORKSHEET, { fetchPolicy: 'no-cache' });
  const [downloadingWorksheet, setDownloadingWorksheet] = useState(false);
  const [draftMenuAnchor, setDraftMenuAnchor] = useState(null);

  const farmClarionDoorIds = useMemo(() => getFarmClarionDoorIds(segment.farmClarionDoorIds), [segment]);

  useEffect(() => {
    if (active) {
      switchSegment(segment.segmentId);
    }
  }, [active, switchSegment, segment.segmentId]);
  const draftLabel = isNewChange ? 'Draft for new change' : 'Draft for existing change';
  const label = isDraft
    ? `${draftLabel} ${dateFormatter(segment.startDate)}`
    : `Start Date ${dateFormatter(segment.startDate)}`;

  const headingLabel = isMutual ? laymanDateFormatter(segment.startDate) : label;
  const draftStatus = getDraftStatus(preview);
  const canDeleteDraft = draftStatus !== DRAFT_STATUS.DENIED && draftStatus !== DRAFT_STATUS.DELETED;
  const openDraftModal = (modal) => {
    setDraftMenuAnchor(null);
    setActivePolicyModal(modal);
  };

  const changes = useMemo(() => {
    return getChanges(segment);
  }, [segment]);

  const timelineTracker = classNames([classes.segmentTimelineTracker, { [classes.lockedTimeline]: isLocked }]);
  const listItem = classNames([classes.segmentsListItem, classes.versionsListItem]);
  const circleIcon = classNames([classes.dot, classes.timelineDot, classes.segmentDot]);

  const handleDownloadFarmWorksheet = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      // The worksheet must be fetched under the release this segment was rated under. Segments rated
      // before that release was persisted carry none; on the production instance the backend can
      // still locate them (prod-requests path), so let it decide and surface its re-rate message
      // only when it can't (release-pinned instances) (issue #33899).
      const { clarionDoorRelease } = segment;
      setDownloadingWorksheet(true);
      try {
        const { data, error } = await fetchRatingWorksheet({
          variables: { clarionDoorIds: farmClarionDoorIds, clarionDoorRelease }
        });
        if (error) {
          throw error;
        }
        if (data?.getRatingWorksheet) {
          window.open(data.getRatingWorksheet, '_blank');
        }
      } catch (err) {
        const message = isRerateError(err)
          ? WORKSHEET_RERATE_MESSAGE
          : 'Failed to download rating worksheet. Try again.';
        toast.notify({ type: 'error', message });
      } finally {
        setDownloadingWorksheet(false);
      }
    },
    [farmClarionDoorIds, segment, fetchRatingWorksheet, toast]
  );

  const changePremiumFormatter = useCallback(
    (value) => {
      // Non-farm: existing ClarionDoor portal link
      if (segment.clarionDoorId) {
        return (
          <a
            className={classes.clarionDoorLink}
            href={`${clarionDoorURL}${segment.clarionDoorId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </a>
        );
      }

      // Farm: download rating worksheet (internal staff + UW approvers)
      if ((!isAgency || canApproveUWReview) && farmClarionDoorIds.length > 0) {
        return (
          <a className={classes.clarionDoorLink} href="#" onClick={handleDownloadFarmWorksheet}>
            {downloadingWorksheet ? 'Downloading...' : value}
          </a>
        );
      }

      return value;
    },
    [
      segment,
      classes,
      farmClarionDoorIds,
      handleDownloadFarmWorksheet,
      downloadingWorksheet,
      isAgency,
      canApproveUWReview
    ]
  );

  const handleDownloadPdf = useCallback(async () => {
    const result = await downloadAutoPremiumBreakdownPdf(policyId, accountId, segment.segmentId);

    if (result.error) {
      toast.notify({ type: 'error', message: result.error });
    } else {
      toast.notify({ type: 'success', message: 'PDF generated successfully' });
    }
  }, [downloadAutoPremiumBreakdownPdf, policyId, accountId, segment.segmentId, toast]);

  const handleClick = () => {
    // Can't navigate to other segments in policy preview mode
    if (!!preview && !isDraft) {
      return;
    }

    onClick();
  };

  return (
    <li className={listItem}>
      {lastChild ? (
        <div className={classNames(classes.segmentsBottomWrapper, { [classes.lockedTimeline]: isLocked })}>
          <IconButton
            color="secondary"
            onClick={handleClick}
            className={classNames(circleIcon, { [classes.activeDot]: active })}
          />
          <span className={classes.segmentsBottomLine} />
        </div>
      ) : (
        <div className={timelineTracker}>
          <IconButton
            color="secondary"
            onClick={handleClick}
            className={classNames(circleIcon, { [classes.activeDot]: active })}
          />
        </div>
      )}

      <div className={classes.segmentContent} onClick={handleClick}>
        <h4 className={isMutual ? classes.timelineSubtitleMutual : classes.timelineSubtitle}>
          {headingLabel}
          {isLocked && (
            <Tooltip
              text="Past endorsements are locked while you're in a draft. Open the official policy to view them."
              smallIcon={{ src: icLocked }}
              className={classes.lockIcon}
              placement="top"
            />
          )}
        </h4>
        <Card
          key={segment.segmentId}
          className={classNames([
            classes.vItemCard,
            classes.vItemSegment,
            { [classes.activeCard]: active },
            { [classes.vItemCardDraft]: isDraft }
          ])}
        >
          {isDraft && (
            <div className={classes.draftHeader}>
              <div className={classes.draftHeaderLeft}>
                <span className={classes.draftLabelGroup}>
                  <span className={classes.draftDot} />
                  <span className={classes.draftHeaderText}>Draft</span>
                  <Tooltip
                    text="This draft hasn't been finalized. It's either a fresh change or a revision of an existing one."
                    smallIcon={{ src: InfoIcon }}
                    className={classes.draftHelpIcon}
                    placement="top"
                  />
                </span>
                <StatusPill status={draftStatus} />
              </div>
              {preview?.draft && (
                <>
                  <IconButton
                    size="small"
                    className={classes.draftMenuButton}
                    aria-label="Draft actions"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDraftMenuAnchor(e.currentTarget);
                    }}
                  >
                    <img src={icMenu} alt="" className={classes.draftMenuIcon} />
                  </IconButton>
                  {/* Rendered in a portal, but React still bubbles its clicks to the segment's onClick. */}
                  <Menu
                    anchorEl={draftMenuAnchor}
                    open={Boolean(draftMenuAnchor)}
                    onClose={() => setDraftMenuAnchor(null)}
                    onClick={(e) => e.stopPropagation()}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  >
                    <MenuItem
                      className={classes.draftMenuItem}
                      onClick={() => openDraftModal(PolicyModalTypes.DRAFT_NOTES)}
                    >
                      View notes
                    </MenuItem>
                    {canDeleteDraft && (
                      <MenuItem
                        className={classes.draftMenuItem}
                        onClick={() => openDraftModal(PolicyModalTypes.DRAFT_DELETE)}
                      >
                        Delete draft
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </div>
          )}
          <div
            className={classNames(isDraft ? classes.draftCardContent : undefined, {
              [classes.lockedContent]: isLocked
            })}
          >
            {isMutual ? (
              <MutualSegmentContent
                segment={segment}
                isDraft={isDraft}
                createdByUsername={preview?.createdBy?.username}
                createdDateTime={preview?.changeDetails?.updatedDateTime}
                changePremiumFormatter={changePremiumFormatter}
              />
            ) : (
              <>
                {changes ? (
                  <ValueField className={classes.valueFieldContent} label="Items Changed" value={changes} />
                ) : (
                  'Initial Segment'
                )}
                <ValueField
                  className={classes.valueFieldContent}
                  label="Policy Premium"
                  value={currencyFormatter(segment.premium)}
                />
                <ValueField
                  className={classNames(classes.valueFieldInline, classes.valueFieldContent)}
                  label="Daily Rate"
                  value={currencyFormatter(segment.dailyRate)}
                />
                <img src={timesIcon} alt="Times" className={classes.timesIcon} />
                <ValueField
                  className={classNames(classes.valueFieldInline, classes.valueFieldContent)}
                  label="Days"
                  value={segment.segmentDays}
                />
                <img src={equalsIcon} alt="Equals" className={classes.equalsIcon} />
                <ValueField
                  className={classNames(classes.valueFieldInline, classes.valueFieldContent)}
                  label="Change Premium"
                  formatter={changePremiumFormatter}
                  value={currencyFormatter(segment.segmentPremium)}
                />
                {policyType === policyTypeLookup.Auto && policyDetails && policyDetails.cars && (
                  <AutoBreakdownPreview
                    triggerText="Premium Breakdown"
                    offer={offer}
                    policyType={policyType}
                    autoCoverage={policyDetails.autoCoverage || offer?.quote?.autoCoverage}
                    premiumBreakdown={JSON.parse(premiumBreakdown || '{}')}
                    cars={policyDetails.cars}
                    trailers={policyDetails.trailers}
                    onDownloadPdf={handleDownloadPdf}
                    downloadingPdf={downloadingAutoPremiumBreakdown}
                  />
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </li>
  );
};

SegmentItem.propTypes = {
  segment: PropTypes.object.isRequired,
  active: PropTypes.bool.isRequired,
  lastChild: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  store: PropTypes.object.isRequired,
  policyType: PropTypes.string.isRequired,
  isDraft: PropTypes.bool
};

export default withStore(SegmentItem);
